/**
 * Content checker list page.
 * 
 * @module pages/ContentPage
 * @see CSDEV-144 - Content/FAQ/LLMs pages: add business filter UI
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, Pagination, Card } from '../components/ui';
import { useContentChecker, usePagination, useAuditLog, useAllBusinesses, useExclusionRules } from '../hooks';
import { AuditActionTypes } from '../lib/audit';
import { isExcludedUser } from '../config/excluded-users';
import { formatDate, formatPercentage } from '../lib/utils';
import type { ContentCheckerWithBusiness, DataTableColumn } from '../types';

/**
 * Content checker list page component.
 * 
 * @returns Content page element
 */
export function ContentPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, pageSize, goToPage, changePageSize, pageSizeOptions } = usePagination();
  const { logAction } = useAuditLog();
  const [internalFilter, setInternalFilter] = useState<'all' | 'external' | 'internal'>('external');

  // Get business filter from URL
  const businessFilter = searchParams.get('business') || '';

  // Fetch filter options
  const { data: businesses } = useAllBusinesses();
  const { data: exclusionRules, isLoading: exclusionRulesLoading } = useExclusionRules();

  const isFilteringByInternal = internalFilter !== 'all';
  const { data, isLoading, error } = useContentChecker({
    page: isFilteringByInternal ? 1 : page,
    pageSize: isFilteringByInternal ? 10000 : pageSize,
    businessId: businessFilter || undefined,
  });

  const filteredData = data?.data.filter((item) => {
    if (internalFilter === 'all') return true;
    const excluded = isExcludedUser(item.businesses?.users?.email, exclusionRules);
    return internalFilter === 'internal' ? excluded : !excluded;
  });

  /**
   * Updates URL search params for filters.
   */
  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
    goToPage(1);
  };

  // Log page view
  useEffect(() => {
    logAction(AuditActionTypes.VIEW_CONTENT_CHECKER_LIST);
  }, [logAction]);

  const columns: DataTableColumn<ContentCheckerWithBusiness>[] = [
    {
      key: 'businesses',
      header: 'Business',
      render: (item) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (item.businesses?.id) {
              navigate(`/businesses/${item.businesses.id}`);
            }
          }}
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          {item.businesses?.business_name || '—'}
        </button>
      ),
    },
    {
      key: 'url',
      header: 'URL',
      render: (item) => (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-secondary-600 hover:text-secondary-700 truncate block max-w-xs"
          onClick={(e) => e.stopPropagation()}
        >
          {item.url}
        </a>
      ),
    },
    {
      key: 'answer_readiness_score',
      header: 'Answer Readiness',
      align: 'right',
      render: (item) => formatPercentage(item.answer_readiness_score),
    },
    {
      key: 'conversational_tone_score',
      header: 'Conversational Tone',
      align: 'right',
      render: (item) => formatPercentage(item.conversational_tone_score),
    },
    {
      key: 'recommendations',
      header: 'Recommendations',
      align: 'center',
      render: (item) => item.recommendations?.length || 0,
    },
    {
      key: 'created_at',
      header: 'Date',
      sortable: true,
      render: (item) => (
        <span className="text-gray-500">{formatDate(item.created_at)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Content Checker</h1>
        <p className="text-gray-500">
          View content analysis results and AI readiness scores
        </p>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Business filter */}
          <select
            value={businessFilter}
            onChange={(e) => updateFilter('business', e.target.value)}
            className="input w-full sm:w-64"
          >
            <option value="">All businesses</option>
            {businesses?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.business_name}
              </option>
            ))}
          </select>

          {/* Internal/External filter */}
          <select
            value={internalFilter}
            onChange={(e) => {
              setInternalFilter(e.target.value as 'all' | 'external' | 'internal');
              goToPage(1);
            }}
            disabled={exclusionRulesLoading}
            className="input w-full sm:w-48"
          >
            <option value="all">All results</option>
            <option value="external">External only</option>
            <option value="internal">Internal only</option>
          </select>

          {/* Clear filters button */}
          {(businessFilter || internalFilter !== 'external') && (
            <button
              onClick={() => {
                setSearchParams({});
                setInternalFilter('external');
              }}
              className="btn-secondary text-sm"
            >
              Clear filter
            </button>
          )}
        </div>
      </Card>

      {/* Error state */}
      {error && (
        <Card>
          <p className="text-error-600">Failed to load content checker: {error.message}</p>
        </Card>
      )}

      {/* Data table */}
      <Card padding="none">
        <DataTable
          columns={columns}
          data={filteredData || []}
          isLoading={isLoading}
          emptyMessage="No content checker results found"
          keyExtractor={(item) => item.id}
        />
        {data && isFilteringByInternal ? (
          <div className="px-4 py-3 text-sm text-gray-500 border-t border-gray-200">
            {filteredData?.length ?? 0} {internalFilter} result{(filteredData?.length ?? 0) !== 1 ? 's' : ''} found
          </div>
        ) : data && data.totalPages > 0 ? (
          <Pagination
            currentPage={page}
            totalPages={data.totalPages}
            onPageChange={goToPage}
            pageSize={pageSize}
            onPageSizeChange={changePageSize}
            totalItems={data.count}
            pageSizeOptions={pageSizeOptions}
          />
        ) : null}
      </Card>
    </div>
  );
}
